/**
 * Enhanced Standardized Error Handler
 *
 * This service provides consistent error handling across the entire application
 * with proper error categorization, logging, user-friendly messages, and
 * compatibility with legacy error handling systems.
 */

import {AppError, ErrorType} from '../../Data/config/AppError';
import {ErrorContextType} from '../../Presentation/contexts/ErrorContext';
import {useNotificationStore} from '../../features/notifications/store/notificationStore';
import {TokenStore} from '../security/TokenStore';
import {AxiosError} from 'axios';

export interface ApplicationError {
  code: string;
  message: string;
  userMessage: string;
  details?: unknown;
  timestamp: Date;
  category: 'network' | 'validation' | 'authorization' | 'authentication' | 'server' | 'not_found' | 'unknown';
  type?: ErrorType;
  originalError?: unknown;
}

export interface ErrorHandlingConfiguration {
  enableLogging: boolean;
  enableNotifications: boolean;
  retryAttempts: number;
  retryDelay: number;
}

class StandardizedErrorHandler {
  private static instance: StandardizedErrorHandler;
  private configuration: ErrorHandlingConfiguration;
  private errorContext: ErrorContextType | null = null;

  private constructor () {
    this.configuration = {
      enableLogging: true,
      enableNotifications: true,
      retryAttempts: 3,
      retryDelay: 1000
    };
  }

  public static getInstance (): StandardizedErrorHandler {
    if (!StandardizedErrorHandler.instance) {
      StandardizedErrorHandler.instance = new StandardizedErrorHandler();
    }
    return StandardizedErrorHandler.instance;
  }

  public configureConfiguration (configuration: Partial<ErrorHandlingConfiguration>): void {
    this.configuration = {...this.configuration, ...configuration};
  }

  /**
   * Set error context for global error handling
   */
  public setErrorContext (context: ErrorContextType): void {
    this.errorContext = context;
  }

  /**
   * Convert any error to AppError without handling/notifying
   * Useful for store layer to convert errors before storing
   */
  public toAppError (error: unknown): AppError {
    if (error instanceof AppError) {
      return error;
    }
    return AppError.fromApiError(error);
  }

  /**
   * Handle and categorize errors consistently
   * Supports both AppError and generic errors for backward compatibility
   */
  public handleError (error: unknown, context?: string): ApplicationError {
    // Handle AppError instances (legacy compatibility)
    if (error instanceof AppError) {
      return this.handleAppError(error);
    }

    // Handle Axios errors - extract stat_message from response.data FIRST
    if (this.isAxiosError(error)) {
      const axiosError = error as AxiosError<any>;
      const responseData = axiosError.response?.data;

      // Extract stat_message from response data
      if (responseData?.stat_message) {
        const appError = AppError.fromApiError(axiosError);
        return this.handleAppError(appError);
      }
    }

    // Handle structured API errors from UserAPIDataSource
    if (error instanceof Error && (error as any).isApiError) {
      return this.handleApiError(error);
    }

    // Handle network errors
    if (error instanceof Error && (error as any).isNetworkError) {
      return this.handleNetworkError(error);
    }

    const applicationError = this.categorizeError(error, context);

    if (this.configuration.enableLogging) {
      this.logError(applicationError);
    }

    if (this.configuration.enableNotifications) {
      this.showUserNotification(applicationError);
    }

    return applicationError;
  }

  /**
   * Handle structured API errors from UserAPIDataSource
   */
  private handleApiError (apiError: Error): ApplicationError {
    const status = (apiError as any).status;
    const response = (apiError as any).response;

    // Extract the specific backend error message
    let backendMessage = apiError.message;
    if (response && response.stat_message) {
      backendMessage = response.stat_message;
    }

    const applicationError: ApplicationError = {
      code: `API_ERROR_${status}`,
      message: backendMessage,
      userMessage: backendMessage, // Preserve the specific backend message
      details: response,
      timestamp: new Date(),
      category: this.mapHttpStatusToCategory(status),
      originalError: apiError
    };

    if (this.configuration.enableLogging) {
      this.logError(applicationError);
    }

    if (this.configuration.enableNotifications) {
      this.showUserNotification(applicationError);
    }

    return applicationError;
  }

  /**
   * Handle network errors
   */
  private handleNetworkError (networkError: Error): ApplicationError {
    const applicationError: ApplicationError = {
      code: 'NETWORK_ERROR',
      message: 'خطأ في الاتصال بالخادم',
      userMessage: 'خطأ في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.',
      details: (networkError as any).originalError,
      timestamp: new Date(),
      category: 'network',
      originalError: networkError
    };

    if (this.configuration.enableLogging) {
      this.logError(applicationError);
    }

    if (this.configuration.enableNotifications) {
      this.showUserNotification(applicationError);
    }

    return applicationError;
  }


  /**
   * Map HTTP status codes to error categories
   */
  private mapHttpStatusToCategory (status: number): ApplicationError['category'] {
    if (status >= 400 && status < 500) {
      if (status === 401) {return 'authentication';}
      if (status === 403) {return 'authorization';}
      if (status === 404) {return 'not_found';}
      if (status === 409) {return 'validation';} // Conflict/duplicate
      return 'validation';
    }
    if (status >= 500) {return 'server';}
    return 'unknown';
  }

  /**
   * Handle AppError instances for backward compatibility
   */
  private handleAppError (appError: AppError): ApplicationError {
    const applicationError = this.convertAppErrorToApplicationError(appError);

    if (this.configuration.enableLogging) {
      this.logError(applicationError);
    }

    // Handle authentication/authorization errors via ErrorContext (for unified display)
    // All errors now go through unified notification system
    if (appError.type === ErrorType.AUTHENTICATION || appError.type === ErrorType.AUTHORIZATION) {
      if (this.errorContext) {
        this.errorContext.showError(appError);
      } else {
        // Fallback to notification if ErrorContext not available
        this.showUserNotification(applicationError);
      }
    } else {
      this.showUserNotification(applicationError);
    }

    return applicationError;
  }

  /**
   * Categorize errors into standard types
   */
  private categorizeError (error: unknown, context?: string): ApplicationError {
    const timestamp = new Date();

    if (error instanceof Error) {
      return this.categorizeErrorInstance(error, timestamp, context);
    }

    // Non-Error objects
    return {
      code: 'UNKNOWN_ERROR',
      message: 'حدث خطأ غير متوقع',
      userMessage: 'حدث خطأ غير متوقع',
      details: error,
      timestamp,
      category: 'unknown',
      originalError: error
    };
  }

  private categorizeErrorInstance (error: Error, timestamp: Date, _context?: string): ApplicationError {
    const {message, stack} = error;

    // Network errors
    if (message.includes('fetch') || message.includes('network') || message.includes('Network Error')) {
      return {
        code: 'NETWORK_ERROR',
        message: 'خطأ في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.',
        userMessage: 'خطأ في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.',
        details: stack,
        timestamp,
        category: 'network',
        type: ErrorType.NETWORK,
        originalError: error
      };
    }

    // Authentication errors
    if (message.includes('401') || message.includes('unauthorized') || message.includes('authentication')) {
      return {
        code: 'AUTHENTICATION_ERROR',
        message: 'خطأ في المصادقة. يرجى تسجيل الدخول مرة أخرى.',
        userMessage: 'خطأ في المصادقة. يرجى تسجيل الدخول مرة أخرى.',
        details: stack,
        timestamp,
        category: 'authentication',
        type: ErrorType.AUTHENTICATION,
        originalError: error
      };
    }

    // Authorization errors
    if (message.includes('403') || message.includes('forbidden') || message.includes('authorization')) {
      return {
        code: 'AUTHORIZATION_ERROR',
        message: 'ليس لديك صلاحية للوصول إلى هذا المورد.',
        userMessage: 'ليس لديك صلاحية للوصول إلى هذا المورد.',
        details: stack,
        timestamp,
        category: 'authorization',
        type: ErrorType.AUTHORIZATION,
        originalError: error
      };
    }

    // Not found errors
    if (message.includes('404') || message.includes('not found')) {
      return {
        code: 'NOT_FOUND_ERROR',
        message: 'لم يتم العثور على البيانات المطلوبة.',
        userMessage: 'لم يتم العثور على البيانات المطلوبة.',
        details: stack,
        timestamp,
        category: 'not_found',
        type: ErrorType.NOT_FOUND,
        originalError: error
      };
    }

    // Conflict/duplicate errors (409)
    if (message.includes('409') || message.includes('conflict') || message.includes('duplicate') || message.includes('already exists')) {
      return {
        code: 'CONFLICT_ERROR',
        message: message || 'البيانات موجودة مسبقاً. يرجى استخدام قيم مختلفة.',
        userMessage: message || 'البيانات موجودة مسبقاً. يرجى استخدام قيم مختلفة.',
        details: stack,
        timestamp,
        category: 'validation',
        type: ErrorType.VALIDATION,
        originalError: error
      };
    }

    // Validation errors - but preserve specific backend messages
    if (message.includes('validation') || message.includes('required') || message.includes('400')) {
      // Check if this is a specific backend error message (contains Arabic text)
      const isBackendMessage = /[\u0600-\u06FF]/.test(message) && message.length > 20;

      return {
        code: 'VALIDATION_ERROR',
        message: isBackendMessage ? message : 'خطأ في البيانات المدخلة. يرجى التحقق من صحة البيانات.',
        userMessage: isBackendMessage ? message : 'خطأ في البيانات المدخلة. يرجى التحقق من صحة البيانات.',
        details: stack,
        timestamp,
        category: 'validation',
        type: ErrorType.VALIDATION,
        originalError: error
      };
    }

    // Server errors
    if (message.includes('500') || message.includes('server') || message.includes('internal')) {
      return {
        code: 'SERVER_ERROR',
        message: 'خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.',
        userMessage: 'خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.',
        details: stack,
        timestamp,
        category: 'server',
        type: ErrorType.SERVER,
        originalError: error
      };
    }

    // Generic error - preserve backend messages
    const isBackendMessage = /[\u0600-\u06FF]/.test(message || '') && (message || '').length > 10;
    return {
      code: 'UNKNOWN_ERROR',
      message: isBackendMessage ? message : (message || 'حدث خطأ غير متوقع'),
      userMessage: isBackendMessage ? message : (message || 'حدث خطأ غير متوقع'),
      details: stack,
      timestamp,
      category: 'unknown',
      type: ErrorType.UNKNOWN,
      originalError: error
    };
  }

  /**
   * Check if error is an Axios error
   */
  private isAxiosError (error: unknown): error is AxiosError {
    return (
      error !== null &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      (error as any).isAxiosError === true
    );
  }

  /**
   * Log errors with context
   */
  private logError (error: ApplicationError): void {
    // Error logging is handled by the error handling system
    // No console logging in production
  }

  /**
   * Show user-friendly notifications
   * Uses Zustand store for unified notification system
   */
  private showUserNotification (error: ApplicationError): void {
    const title = this.getErrorTitle(error);
    const showAsWarning = this.shouldShowAsWarning(error);

    // Use Zustand store directly (not via hook, since we're in a service class)
    const store = useNotificationStore.getState();
    store.addNotification({
      type: showAsWarning ? 'warning' : 'error',
      message: error.userMessage,
      title,
      duration: 5000
    });
  }

  /**
   * Get error title based on category
   */
  private getErrorTitle (error: ApplicationError): string {
    switch (error.category) {
      case 'network':
        return 'خطأ في الاتصال';
      case 'authentication':
        return 'خطأ في المصادقة';
      case 'authorization':
        return 'خطأ في الصلاحيات';
      case 'validation':
        return 'خطأ في التحقق';
      case 'not_found':
        return 'لم يتم العثور على البيانات';
      case 'server':
        return 'خطأ في الخادم';
      default:
        return 'خطأ غير متوقع';
    }
  }

  /**
   * Determine if error should be shown as warning
   */
  private shouldShowAsWarning (error: ApplicationError): boolean {
    return error.category === 'validation' || error.category === 'not_found';
  }

  /**
   * Get user-friendly error message
   */
  public getUserMessage (error: ApplicationError): string {
    return error.message;
  }

  /**
   * Check if error is retryable
   */
  public isRetryable (error: ApplicationError): boolean {
    return error.category === 'network' || error.category === 'server';
  }

  /**
   * Get retry configuration
   */
  public getRetryConfiguration (): { attempts: number; delay: number } {
    return {
      attempts: this.configuration.retryAttempts,
      delay: this.configuration.retryDelay
    };
  }

  /**
   * Convert AppError to ApplicationError for backward compatibility
   */
  private convertAppErrorToApplicationError (appError: AppError): ApplicationError {
    const category = this.mapErrorTypeToCategory(appError.type);

    return {
      code: appError.type,
      message: appError.message,
      userMessage: appError.message,
      details: appError.details,
      timestamp: new Date(),
      category,
      type: appError.type,
      originalError: appError
    };
  }

  /**
   * Map ErrorType to category
   */
  private mapErrorTypeToCategory (type: ErrorType): ApplicationError['category'] {
    switch (type) {
      case ErrorType.NETWORK:
        return 'network';
      case ErrorType.AUTHENTICATION:
        return 'authentication';
      case ErrorType.AUTHORIZATION:
        return 'authorization';
      case ErrorType.VALIDATION:
        return 'validation';
      case ErrorType.NOT_FOUND:
        return 'not_found';
      case ErrorType.SERVER:
        return 'server';
      default:
        return 'unknown';
    }
  }

  /**
   * Handle authentication errors (redirect to login)
   */
  private handleAuthenticationError (): void {
    TokenStore.clear();

    // Only redirect to login if we're not already on the login page
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
  }

  /**
   * Handle authorization errors
   */
  private handleAuthorizationError (): void {
    // Show access denied message or redirect to appropriate page
    // This can be customized based on your application's needs
  }

  /**
   * Check if error is network error
   */
  public isNetworkError (error: unknown): boolean {
    if (error instanceof AppError) {
      return error.type === ErrorType.NETWORK;
    }
    return false;
  }

  /**
   * Check if error is authentication error
   */
  public isAuthenticationError (error: unknown): boolean {
    if (error instanceof AppError) {
      return error.type === ErrorType.AUTHENTICATION;
    }
    return false;
  }

  /**
   * Check if error is authorization error
   */
  public isAuthorizationError (error: unknown): boolean {
    if (error instanceof AppError) {
      return error.type === ErrorType.AUTHORIZATION;
    }
    return false;
  }

  /**
   * Check if error is validation error
   */
  public isValidationError (error: unknown): boolean {
    if (error instanceof AppError) {
      return error.type === ErrorType.VALIDATION;
    }
    return false;
  }
}

export default StandardizedErrorHandler;
